import {
  FieldSelect,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { Component } from 'react';
import { Field, Form, InjectedFormProps, reduxForm } from 'redux-form';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldDuration } from 'fable/fieldDuration';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { durationConverter } from 'utils/duration';

import type { HideStrategyModalStatePropsT } from '../../containers/modals/hidestrategy';
import type {
  FlowChatNodeHideStrategyFormValues,
  FlowChatNodeHideStrategyPayloadT,
  FlowChatNodeHideStrategyT,
} from '../nodeTypes';
import { HideStrategy } from '../types';

import styles from './hidestrategy.module.scss';

type PropsT = FlowManagerEditModalComponentT<FlowChatNodeHideStrategyT> &
  HideStrategyModalStatePropsT;

type InjectedProps = InjectedFormProps<
  FlowChatNodeHideStrategyFormValues,
  PropsT
>;

class HideStrategyModal extends Component<PropsT & InjectedProps> {
  isHideStrategyNode(node: PropsT['node']): node is FlowChatNodeHideStrategyT {
    return node.nodeType !== 'action';
  }

  @boundMethod
  getPayload(
    strategy: HideStrategy,
    ewtThreshold: number,
  ): FlowChatNodeHideStrategyPayloadT {
    const { node } = this.props;

    const shownNodeId = this.isHideStrategyNode(node)
      ? node.payload.shownNodeId
      : uuid();
    const hiddenNodeId =
      this.isHideStrategyNode(node) &&
      node.payload.strategy !== HideStrategy.NEVER
        ? node.payload.hiddenNodeId
        : uuid();

    switch (strategy) {
      case HideStrategy.NEVER:
        return {
          strategy,
          shownNodeId,
        };
      case HideStrategy.EXPECTED_WAITING_TIME:
        return {
          strategy,
          shownNodeId,
          hiddenNodeId,
          ewtThreshold: durationConverter.seconds.toMilliseconds(ewtThreshold),
        };
      case HideStrategy.ALL_AGENTS_AWAY:
      case HideStrategy.ALL_AGENTS_OFFLINE:
      default:
        return {
          strategy,
          shownNodeId,
          hiddenNodeId,
        };
    }
  }

  @boundMethod
  handleSaveClick(values: FlowChatNodeHideStrategyFormValues) {
    // The form starts out with a null value, the form validates it to not be null.
    // But because it's a possible value according to the type, we rule it out first.
    if (values.strategy === null) return;

    const { node } = this.props;
    const nodeOptions: FlowChatNodeHideStrategyT = {
      ...node,
      badges: [],
      icon: '',
      isAction: false,
      isEditable: true,
      isRemovable: true,
      nodeType: FlowNodeTypes.HIDE_STRATEGY,
      payload: this.getPayload(values.strategy, values.ewtThreshold),
      overlay: [],
      text: '',
    };

    this.props.replaceNode(nodeOptions);
    this.props.triggerClose();
  }

  @boundMethod
  closeModal() {
    const { destroy, triggerClose } = this.props;
    destroy();
    triggerClose();
  }

  validateStrategy(strategy: FlowChatNodeHideStrategyFormValues['strategy']) {
    if (strategy === null) {
      return 'Must pick a hide strategy';
    }
    return undefined;
  }

  validateDuration(duration: number) {
    if (duration >= durationConverter.days.toSeconds(1)) {
      return 'Must be 1 day or less';
    }
    if (duration <= 0) {
      return 'Must be at least 1 second';
    }
    return undefined;
  }

  render() {
    const {
      handleSubmit,
      hidestrategyValues,
      node,
      nodeLabel,
      selectedStrategy,
    } = this.props;

    return (
      <div className="queue modal-chatflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.closeModal}>
            Hide strategy is used to decide how the widget functions when all
            agents are offline and/or away. We recommend that you only use
            &apos;Never hide&apos; if you also use a contact form.
          </ModalHead>
          <ModalBody>
            <Field
              label="Hide strategy"
              placeholder="Please select a hide strategy"
              name="strategy"
              validate={this.validateStrategy}
              component={FieldSelect}
              items={hidestrategyValues}
              required
            />
            {selectedStrategy === HideStrategy.EXPECTED_WAITING_TIME && (
              <Field
                component={FieldDuration}
                name="ewtThreshold"
                label="EWT Threshold"
                className={styles.duration}
                includeDays={false}
                validate={this.validateDuration}
                required
              />
            )}
          </ModalBody>
          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            cancelOnClick={this.closeModal}
          />
        </Form>
      </div>
    );
  }
}

export default reduxForm<FlowChatNodeHideStrategyFormValues, PropsT>({
  form: 'chatFlowHideStrategyForm', // A unique identifier for this form.
  enableReinitialize: true,
})(HideStrategyModal);
